export interface ImportDefinition {
  id: number;
  name: string;
  description?: string;
  targetTable: string;
  sourceId?: number | null;
  scheduleType: 'manual' | 'daily' | 'weekly' | 'monthly';
  lastRunAt?: string;
  lastRunStatus: 'pending' | 'success' | 'failed' | 'partial';
  isActive: boolean;
  createdAt: string;
}

export interface AllowedTable {
  id: number;
  tableName: string;
  displayName: string;
  description?: string;
  requiredFields: string[];
  uniqueKeyFields: string[];
}

export interface TableField {
  name: string;
  type: string;
  nullable: boolean;
  isPrimaryKey: boolean;
  isForeignKey: boolean;
  foreignKeyTable?: string;
  maxLength?: number;
  comment?: string;
}

export interface FilePreviewColumn {
  index: number;
  name: string;
  originalName: string;
  sampleValues: string[];
  detectedType: 'string' | 'number' | 'date' | 'boolean' | 'unknown';
}

export interface HeaderCellSplitPart {
  group: number;
  asColumn: string;
  targetField?: string;
}

export interface HeaderCellBinding {
  cell: string;
  asColumn: string;
  targetField?: string;
  split?: {
    pattern: string;
    parts: HeaderCellSplitPart[];
  };
}

export interface FilePreview {
  filename: string;
  format: string;
  encoding: string;
  totalRows: number;
  columns: FilePreviewColumn[];
  previewRows: Record<string, string>[];
  errors?: string[];
  preambleRows?: string[][];
  headerCellValues?: Record<string, string>;
}

export const indexToColLetters = (index: number): string => {
  let n = index + 1;
  let s = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

export interface SourcePreviewInfo {
  id: number;
  sourceId: number;
  originalFilename: string;
  fileDate?: string;
  createdAt?: string;
}

export interface FtpFileInfo {
  name: string;
  size: number;
  modifiedAt?: string;
  type: 'file' | 'directory' | 'unknown';
  path: string;
}

export interface ImportSource {
  id: number;
  type: 'mailbox' | 'sftp' | 'local';
  name: string;
  is_active: number | boolean;
  last_fetch_at?: string | null;
  last_fetch_status?: 'success' | 'failed' | null;
  last_fetch_error?: string | null;
  last_fetch_files_count?: number | null;
}

export interface FieldMapping {
  id: string;
  targetField: string;
  type: 'simple' | 'concat' | 'calculation' | 'datetime' | 'constant' | 'decimal' | 'lookup' | 'condition' | 'jsonpath' | 'fuel_type';
  sourceColumns: string[];
  transform?: any;
  transforms?: Array<{ type: string; params?: Record<string, any> }>;
}

export interface HistoryRun {
  id: string;
  definitionId: number;
  definitionName?: string;
  status: string;
  startedAt?: string;
  completedAt?: string;
  sourceFilename?: string;
  rowsTotal: number;
  rowsProcessed: number;
  rowsImported: number;
  rowsUpdated: number;
  rowsSkipped: number;
  rowsFailed: number;
  errorLog: Array<{ row?: number; message?: string }>;
  createdAt: string;
}

export interface DetailRun {
  id: string;
  status: string;
  startedAt?: string;
  completedAt?: string;
  sourceFilename?: string;
  rowsTotal: number;
  rowsImported: number;
  rowsUpdated: number;
  rowsSkipped: number;
  rowsFailed: number;
  errorLog: Array<{ row?: number; message?: string; code?: string }>;
  warningLog?: Array<{ row?: number; message?: string; code?: string }>;
  artifacts?: {
    failedRowsPath?: string | null;
    duplicateRowsPath?: string | null;
  };
  createdAt: string;
}

export interface ImportProgress {
  runId: string;
  processed: number;
  total: number;
  imported: number;
  updated: number;
  skipped: number;
  failed: number;
  status?: string;
  run?: { rowsImported: number; rowsUpdated: number; rowsFailed: number };
  error?: string;
}

export interface SourceFilesModalInfo {
  definitionId: number;
  definitionName: string;
  sourceId: number;
}

export interface SourceFile {
  id: number;
  original_filename: string;
  file_date: string;
  file_size: number | null;
  created_at: string;
}

export interface SchemaValidationError {
  targetField?: string;
  mappingId?: string;
  message: string;
  code: string;
}

// Constants
export const TEST_IMPORT_ROW_LIMIT = 10;
export const CIRCLE_K_DISALLOWED_TARGETS = new Set(['vehicle_id', 'driver_id', 'organization_id', 'customer_id']);
// Fuel-supplier imports never carry DQFleet-internal links; these are resolved later by card mapping.
// customer_id is legacy — use customer_number (supplier account) and organization_id (DQFleet org).
export const FUEL_SUPPLIER_DISALLOWED_TARGETS = new Set(['vehicle_id', 'driver_id', 'organization_id', 'customer_id']);
export const AUTO_MAP_TARGET_ALIASES: Record<string, string[]> = {
  invoice_number: ['fakturanummer', 'fakturanr', 'faktura_nr', 'faktura-nr', 'fakdok-nr', 'vernr', 'invoice', 'invoice_no', 'invoice number'],
  invoice_date: ['fakturadatum', 'faktureringsdatum', 'invoice_date', 'fakturadate', 'invoice date'],
  // OBS: "account number" är OKQ8-kontodel till card_number — inte customer_number.
  customer_number: ['kundnummer', 'kundnr', 'customer_number', 'customer_no', 'konto-id', 'kontoid', 'customer account id', 'customer number'],
  customer_name: ['kundnamn', 'namn', 'efternamn', 'customer_name', 'name'],
  card_number: ['kortnummer', 'kortnr', 'kortlopnummer', 'kortlöpnummer', 'kort', 'card_number', 'card_no', 'card number', 'primärt kort', 'primart kort', 'kortreferens 1', 'kortreferens1', 'kortreferens', 'präglings-id', 'praglings-id', 'magnetstripe'],
  purchased: [
    'tankdatum', 'datum', 'transaktionsdatum', 'transaction_date', 'transactiondate',
    'purchased', 'order-datum', 'orderdatum', 'köpdatum', 'kopdatum', 'kpdatum', 'transaction date', 'tidpunkt',
  ],
  station_number: ['stationsnummer', 'stationnr', 'station_number', 'anl.nr', 'anlaggningsnr', 'merchant id', 'terminal'],
  station_name: ['station', 'stationsnamn', 'station_name', 'inkopsstalle', 'forsaljninsstalle', 'merchant name'],
  product_number: ['produktnummer', 'produktkod', 'transkod', 'product_number', 'product_code', 'product group'],
  product_name: ['produkt', 'produktnamn', 'ptyp', 'product_name', 'product_type', 'product text'],
  quantity: ['liter', 'mangd', 'mängd', 'antal', 'kvantitet', 'qty', 'quantity', 'volym'],
  price_excl_vat: ['prisexklmoms', 'beloppexklmoms', 'belopp', 'forsaljningsvardeinklexkl', 'försäljningsvärdeinkl.exkl', 'netto', 'amount_ex_vat', 'price_excl_vat', 'net amount excl vat', 'aktuellt belopp'],
  vat: ['moms', 'momsbelopp', 'vat'],
};
export const CIRCLE_K_PURCHASE_DATE_ALIASES = ['order-datum', 'kopdatum', 'kpdatum', 'tankdatum', 'datum', 'transaktionsdatum'];
export const CIRCLE_K_PURCHASE_TIME_ALIASES = ['order-tid', 'tid', 'transaction_time'];
export const CIRCLE_K_CUSTOMER_ID_ALIASES = ['konto-id', 'kontoid', 'customer_id', 'customerid', 'kundnr'];
// Preem exports use English headers (international format). "Transaction date" is YYYY-MM-DD,
// "Transaction time" is HH:mm:ss. "Customer Account ID" is the customer identifier.
export const PREEM_PURCHASE_DATE_ALIASES = ['transaction date', 'transactiondate', 'transaktionsdatum', 'datum'];
export const PREEM_PURCHASE_TIME_ALIASES = ['transaction time', 'transactiontime', 'tid'];
export const PREEM_CUSTOMER_ID_ALIASES = ['customer account id', 'customeraccountid', 'customer_id', 'customerid'];
export const OKQ8_PURCHASE_DATE_ALIASES = [
  'purchase date',
  'purchasedate',
  'kopdatum',
  'köpdatum',
  'tankdatum',
  'datum',
  'transaktionsdatum',
];
export const OKQ8_PURCHASE_TIME_ALIASES = [
  'purchase time',
  'purchasetime',
  'koptid',
  'köptid',
  'tid',
  'transaction_time',
];
export const OKQ8_CUSTOMER_ID_ALIASES = ['kundnummer', 'kundnr', 'customer_id', 'customerid', 'customer number', 'customernumber'];
/** OKQ8 card_number = konto/kundnr + löpnummer (serial padLeft 4). EN: Account Number; SV: Kundnummer. */
export const OKQ8_CARD_ACCOUNT_ALIASES = [
  'account number',
  'accountnumber',
  'kontonummer',
  'kontonr',
  'konto',
  'kundnummer',
  'kundnr',
  'customer number',
  'customernumber',
];
export const OKQ8_CARD_SERIAL_ALIASES = [
  'card serial number',
  'cardserialnumber',
  'card line 2',
  'cardline2',
  'kortlopnummer',
  'kortlöpnummer',
  'lopnummer',
  'löpnummer',
];
// St1 (.xlsx): "Tidpunkt" is a single combined "YYYY-MM-DD HH:mm" column (no separate
// time column). No customer_id column — "Kundnummer" maps to customer_number generically.
export const ST1_PURCHASE_DATE_ALIASES = ['tidpunkt', 'datum', 'transaktionsdatum'];
export const ST1_PURCHASE_TIME_ALIASES: string[] = [];
// Qstar/Bilisten "Fordonsspecifikation" .xlsx: header on row 7 (skipRows 6).
// "Datum" is one combined "YYYY-MM-DD HH:mm" column.
export const QSTAR_PURCHASE_DATE_ALIASES = ['datum', 'tidpunkt', 'transaktionsdatum'];
export const QSTAR_PURCHASE_TIME_ALIASES: string[] = [];
// Din-X terminal export: "Tid" is one combined "DD-MM-YYYY HH:mm:ss" column.
export const DINX_PURCHASE_DATE_ALIASES = ['tid', 'datum', 'tidpunkt'];
export const DINX_PURCHASE_TIME_ALIASES: string[] = [];

// Utility functions
export const toTestId = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export const normalizeHeaderName = (value: string): string =>
  value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '');

export const containsAnyToken = (value: string, tokens: string[]): boolean =>
  tokens.some((token) => value.includes(token));

/** Strip Excel-style leading apostrophe the same way backend import does. */
export const stripLeadingApostrophe = (value: unknown): string => {
  const str = String(value ?? '');
  return str.replace(/^'/, '');
};

export const applyTransformPipelineClient = (
  value: any,
  transforms: Array<{ type: string; params?: Record<string, any> }>,
  row: Record<string, string>,
  decimalSeparator: ',' | '.',
) => {
  let current = value;
  for (const step of transforms) {
    const params = step.params || {};
    switch (step.type) {
      case 'trim':
        current = current === null || current === undefined ? current : String(current).trim();
        break;
      case 'uppercase':
        current = current === null || current === undefined ? current : String(current).toUpperCase();
        break;
      case 'lowercase':
        current = current === null || current === undefined ? current : String(current).toLowerCase();
        break;
      case 'substring': {
        if (current === null || current === undefined) break;
        const start = Number(params.start || 0);
        const length = params.length !== undefined ? Number(params.length) : undefined;
        const str = String(current);
        current = length !== undefined ? str.substring(start, start + length) : str.substring(start);
        break;
      }
      case 'regex_replace': {
        if (current === null || current === undefined) break;
        const pattern = params.pattern || '';
        const replacement = params.replacement ?? '';
        const flags = params.flags || 'g';
        const regex = new RegExp(pattern, flags);
        current = String(current).replace(regex, replacement);
        break;
      }
      case 'concat': {
        const values = Array.isArray(params.values) ? params.values : [];
        const separator = params.separator ?? '';
        const padLeft = (params.padLeft && typeof params.padLeft === 'object')
          ? (params.padLeft as Record<string, number | { length?: number; char?: string }>)
          : {};
        const resolvePad = (key: string, raw: string): string => {
          const spec = padLeft[key];
          if (spec === undefined || spec === null) return raw;
          const length = typeof spec === 'number' ? spec : Number(spec.length ?? 0);
          const char = typeof spec === 'number' ? '0' : String(spec.char ?? '0');
          if (!Number.isFinite(length) || length <= 0) return raw;
          return String(raw).padStart(length, char || '0');
        };
        const resolved = values.map((v: string) => {
          const matches = typeof v === 'string' ? v.match(/\{([^}]+)\}/g) : null;
          if (!matches) return String(v);
          let result = String(v);
          matches.forEach((m: string) => {
            const key = m.slice(1, -1);
            const cell = String(row[key] ?? '').replace(/^'/, '').trim();
            result = result.replace(m, resolvePad(key, cell));
          });
          return result;
        });
        current = resolved.join(separator);
        break;
      }
      case 'decimal_convert':
      case 'parse_number': {
        if (current === null || current === undefined) break;
        const fromDecimal = params.fromDecimal ?? decimalSeparator;
        const fromThousand = params.fromThousand ?? (decimalSeparator === ',' ? ' ' : ',');
        let cleaned = String(current).trim();
        if (fromThousand === ' ') cleaned = cleaned.replace(/\s/g, '');
        if (fromThousand === '.') cleaned = cleaned.replace(/\./g, '');
        if (fromThousand === ',') cleaned = cleaned.replace(/,/g, '');
        if (fromDecimal === ',') cleaned = cleaned.replace(',', '.');
        const num = parseFloat(cleaned);
        current = Number.isNaN(num) ? null : num;
        break;
      }
      case 'multiply': {
        const num = Number(current);
        const factor = Number(params.value ?? 1);
        current = Number.isFinite(num) ? num * factor : null;
        break;
      }
      case 'add': {
        const num = Number(current);
        const addVal = Number(params.value ?? 0);
        current = Number.isFinite(num) ? num + addVal : null;
        break;
      }
      case 'subtract_column': {
        const col = typeof params.column === 'string' ? params.column : '';
        if (!col) {
          current = null;
          break;
        }
        const parseAmt = (raw: unknown): number | null => {
          if (raw === null || raw === undefined || raw === '') return null;
          if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
          let cleaned = String(raw).trim().replace(/^\+/, '');
          if (decimalSeparator === ',') {
            cleaned = cleaned.replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
          } else {
            cleaned = cleaned.replace(/\s/g, '').replace(/,/g, '');
          }
          const n = parseFloat(cleaned);
          return Number.isNaN(n) ? null : n;
        };
        const base = parseAmt(current);
        const other = parseAmt(row[col]);
        current = base !== null && other !== null ? base - other : null;
        break;
      }
      case 'divide': {
        const num = Number(current);
        const divisor = Number(params.value ?? 1);
        current = !Number.isFinite(num) || divisor === 0 ? null : num / divisor;
        break;
      }
      case 'round': {
        const num = Number(current);
        const decimals = Number(params.decimals ?? 0);
        if (!Number.isFinite(num)) {
          current = null;
          break;
        }
        const factor = Math.pow(10, decimals);
        current = Math.round(num * factor) / factor;
        break;
      }
      case 'default_value': {
        const onlyIfNull = params.onlyIfNull !== false;
        if (!onlyIfNull || current === null || current === undefined || current === '') {
          current = params.value;
        }
        break;
      }
      case 'date_parse':
      case 'date_format': {
        if (current === null || current === undefined || current === '') break;
        const inputFormat = String(params.inputFormat || '');
        const outputFormat = String(params.outputFormat || 'YYYY-MM-DD');
        const raw = String(current).trim();
        let y = 0;
        let m = 0;
        let d = 0;
        if (inputFormat === 'DD.MM.YYYY' || /^\d{2}\.\d{2}\.\d{4}$/.test(raw)) {
          const parts = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
          if (!parts) {
            current = null;
            break;
          }
          d = Number(parts[1]);
          m = Number(parts[2]);
          y = Number(parts[3]);
        } else if (inputFormat === 'DD-MM-YYYY' || /^\d{2}-\d{2}-\d{4}$/.test(raw)) {
          const parts = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
          if (!parts) {
            current = null;
            break;
          }
          d = Number(parts[1]);
          m = Number(parts[2]);
          y = Number(parts[3]);
        } else if (inputFormat === 'DD/MM/YYYY' || /^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
          const parts = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
          if (!parts) {
            current = null;
            break;
          }
          d = Number(parts[1]);
          m = Number(parts[2]);
          y = Number(parts[3]);
        } else if (inputFormat === 'YYYYMMDD' || /^\d{8}$/.test(raw)) {
          y = Number(raw.slice(0, 4));
          m = Number(raw.slice(4, 6));
          d = Number(raw.slice(6, 8));
        } else if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
          y = Number(raw.slice(0, 4));
          m = Number(raw.slice(5, 7));
          d = Number(raw.slice(8, 10));
        } else {
          const parsed = new Date(raw);
          if (Number.isNaN(parsed.getTime())) {
            current = null;
            break;
          }
          y = parsed.getFullYear();
          m = parsed.getMonth() + 1;
          d = parsed.getDate();
        }
        if (!y || !m || !d) {
          current = null;
          break;
        }
        const yyyy = String(y).padStart(4, '0');
        const mm = String(m).padStart(2, '0');
        const dd = String(d).padStart(2, '0');
        if (outputFormat === 'YYYY-MM-DD HH:mm:ss') {
          current = `${yyyy}-${mm}-${dd} 00:00:00`;
        } else {
          current = `${yyyy}-${mm}-${dd}`;
        }
        break;
      }
      default:
        break;
    }
  }
  return current;
};

/**
 * Preview the value as it will be saved (mirrors backend applyMapping for common types).
 */
export const previewMappedValueAsSaved = (
  mapping: FieldMapping,
  row: Record<string, string>,
  decimalSeparator: ',' | '.',
): unknown => {
  const strip = (v: unknown) => stripLeadingApostrophe(v).trim();
  let value: unknown;

  switch (mapping.type) {
    case 'constant':
      value = mapping.transform?.value ?? null;
      break;
    case 'decimal': {
      const raw = strip(row[mapping.sourceColumns[0]] ?? '');
      if (!raw) {
        value = null;
        break;
      }
      let cleaned = raw;
      if (decimalSeparator === ',') {
        cleaned = cleaned.replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
      } else {
        cleaned = cleaned.replace(/\s/g, '').replace(/,/g, '');
      }
      const num = parseFloat(cleaned);
      value = Number.isNaN(num) ? null : num;
      break;
    }
    case 'datetime': {
      const dt = mapping.transform || {};
      const formatYmd = (dateRaw: string): string | null => {
        if (/^\d{8}$/.test(dateRaw)) {
          return `${dateRaw.slice(0, 4)}-${dateRaw.slice(4, 6)}-${dateRaw.slice(6, 8)}`;
        }
        if (/^\d{4}-\d{2}-\d{2}/.test(dateRaw)) {
          return dateRaw.slice(0, 10);
        }
        // Circle K Nordic CSV: 01.07.2026
        const dmyDot = dateRaw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
        if (dmyDot) {
          return `${dmyDot[3]}-${dmyDot[2]}-${dmyDot[1]}`;
        }
        const dmySlash = dateRaw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (dmySlash) {
          return `${dmySlash[3]}-${dmySlash[2]}-${dmySlash[1]}`;
        }
        // OKQ8 engelska rapporter: 02-05-2025 / 03-07-2026
        const dmyDash = dateRaw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (dmyDash) {
          return `${dmyDash[3]}-${dmyDash[2]}-${dmyDash[1]}`;
        }
        return null;
      };
      /** Mirror backend parseTimeParts for common fuel-file formats. */
      const formatHms = (timeRaw: string): string | null => {
        if (!timeRaw) return '00:00:00';
        // Circle K CSV: +"10.04.54"
        const cleaned = timeRaw
          .trim()
          .replace(/^['"]+|['"]+$/g, '')
          .replace(/^\+/, '')
          .trim();
        if (!cleaned) return '00:00:00';
        if (/^\d{5,6}$/.test(cleaned)) {
          const padded = cleaned.padStart(6, '0');
          const h = padded.slice(0, 2);
          const m = padded.slice(2, 4);
          const s = padded.slice(4, 6);
          if (Number(h) > 23 || Number(m) > 59 || Number(s) > 59) return null;
          return `${h}:${m}:${s}`;
        }
        if (/^\d{3,4}$/.test(cleaned)) {
          const padded = cleaned.padStart(4, '0');
          const h = padded.slice(0, 2);
          const m = padded.slice(2, 4);
          if (Number(h) > 23 || Number(m) > 59) return null;
          return `${h}:${m}:00`;
        }
        const colonOrDot = cleaned.match(/^(\d{1,2})[:.](\d{2})(?:[:.](\d{2}))?$/);
        if (colonOrDot) {
          const h = colonOrDot[1].padStart(2, '0');
          const m = colonOrDot[2];
          const s = (colonOrDot[3] ?? '00').padStart(2, '0');
          if (Number(h) > 23 || Number(m) > 59 || Number(s) > 59) return null;
          return `${h}:${m}:${s}`;
        }
        return null;
      };

      if ((dt.combineDateAndTime || mapping.sourceColumns.length >= 2) && mapping.sourceColumns.length >= 2) {
        const dateRaw = strip(row[mapping.sourceColumns[0]] ?? '');
        const timeRaw = strip(row[mapping.sourceColumns[1]] ?? '');
        const ymd = formatYmd(dateRaw);
        const hms = formatHms(timeRaw);
        value = ymd && hms ? `${ymd} ${hms}` : null;
        break;
      }
      const raw = strip(row[mapping.sourceColumns[0]] ?? '');
      if (!raw) {
        value = null;
        break;
      }
      const ymdOnly = formatYmd(raw);
      value = ymdOnly ? `${ymdOnly} 00:00:00` : raw;
      break;
    }
    case 'calculation': {
      const formulaTemplate = String((mapping.transform as { formula?: string } | undefined)?.formula || '');
      if (!formulaTemplate) {
        value = null;
        break;
      }
      const parseAmt = (raw: string): number => {
        let cleaned = strip(raw).replace(/^\+/, '');
        if (decimalSeparator === ',') {
          cleaned = cleaned.replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
        } else {
          cleaned = cleaned.replace(/\s/g, '').replace(/,/g, '');
        }
        const n = parseFloat(cleaned);
        return Number.isFinite(n) ? n : 0;
      };
      let formula = formulaTemplate;
      for (const col of mapping.sourceColumns) {
        const numVal = parseAmt(row[col] ?? '');
        formula = formula.split(`{${col}}`).join(String(numVal));
      }
      if (!/^[\d\s+\-*/().]+$/.test(formula)) {
        value = null;
        break;
      }
      try {
        // eslint-disable-next-line no-new-func
        value = Function(`"use strict"; return (${formula});`)();
      } catch {
        value = null;
      }
      break;
    }
    case 'concat':
    case 'lookup':
    case 'condition':
    case 'jsonpath':
    case 'fuel_type':
      value = mapping.sourceColumns.length > 1
        ? mapping.sourceColumns.map((col) => strip(row[col] ?? '')).join(' ')
        : strip(row[mapping.sourceColumns[0]] ?? '');
      break;
    case 'simple':
    default: {
      const raw = row[mapping.sourceColumns[0]] ?? '';
      value = strip(raw);
      break;
    }
  }

  if (mapping.transforms && mapping.transforms.length > 0) {
    return applyTransformPipelineClient(value, mapping.transforms, row, decimalSeparator);
  }
  return value;
};
